/**
 * Supported configuration value types. These match the configuration types
 * available when authoring a configuration in Switcheroo / Statsig:
 * Boolean, Number, String, String list, Number list.
 */
export type DynamicConfigType =
    | "boolean"
    | "number"
    | "string"
    | "stringList"
    | "numberList";

export type DynamicConfig = {
    /**
     * The Statsig dynamic config key (matches the key used when the
     * configuration was created in Switcheroo).
     */
    configKey: string;
    /**
     * The value type stored in this dynamic config.
     */
    type: DynamicConfigType;
};

export type DynamicConfigManifest = {
    [configName: string]: DynamicConfig;
};

/**
 * Manifest of all dynamic configurations consumed by WAC.
 *
 * Add new configurations here, then call `getDynamicConfig` with the
 * manifest entry to retrieve the value at runtime.
 */
export const dynamicConfigManifest = {
    wacTestConfigBoolean: {
        configKey: "wac-test-config-boolean",
        type: "boolean"
    },
    wacTestConfigNumber: {
        configKey: "wac-test-config-number",
        type: "number"
    },
    wacTestConfigString: {
        configKey: "wac-test-config-string",
        type: "string"
    },
    wacTestConfigStringList: {
        configKey: "wac-test-config-string-list",
        type: "stringList"
    }
} as const satisfies DynamicConfigManifest;

/**
 * Union of all properties of `dynamicConfigManifest`. Used to type-narrow the
 * argument to `getDynamicConfig`.
 */
export type DynamicConfigManifestProperty =
    (typeof dynamicConfigManifest)[keyof typeof dynamicConfigManifest];
